import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { StatusChip } from "@/components/StatusChip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Users, ArrowRight, SpinnerGap } from "@phosphor-icons/react";

const TIMEZONES = ["UTC", "Africa/Lagos", "Africa/Nairobi", "Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Kolkata", "Asia/Singapore"];

function TurnoutBar({ turnout }) {
  return (
    <div>
      <div className="flex justify-between text-xs font-mono text-zinc-500 mb-1.5">
        <span>TURNOUT</span>
        <span>{turnout.voted}/{turnout.total} · {turnout.pct}%</span>
      </div>
      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full bg-black transition-all" style={{ width: `${turnout.pct}%` }} />
      </div>
    </div>
  );
}

function CreateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ title: "", description: "", starts_at: "", ends_at: "", timezone: "UTC", results_visibility: "after_close" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...f, starts_at: new Date(f.starts_at).toISOString(), ends_at: new Date(f.ends_at).toISOString() };
      const { data } = await api.post("/elections", payload);
      toast.success("Election created");
      setOpen(false);
      onCreated(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-election-btn" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1F6FE5] text-white rounded-md font-semibold text-sm hover:bg-[#1552C9] transition-colors active:scale-95">
          <Plus size={18} weight="bold" /> New election
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Create election</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Title</Label>
            <Input data-testid="election-title" required value={f.title} onChange={set("title")} placeholder="Board Election 2026" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
            <Textarea data-testid="election-desc" value={f.description} onChange={set("description")} placeholder="Purpose of this election…" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Opens</Label>
              <Input data-testid="election-start" type="datetime-local" required value={f.starts_at} onChange={set("starts_at")} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Closes</Label>
              <Input data-testid="election-end" type="datetime-local" required value={f.ends_at} onChange={set("ends_at")} className="mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Timezone</Label>
              <Select value={f.timezone} onValueChange={set("timezone")}>
                <SelectTrigger data-testid="election-tz" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Results</Label>
              <Select value={f.results_visibility} onValueChange={set("results_visibility")}>
                <SelectTrigger data-testid="election-visibility" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="after_close">After close</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <button data-testid="create-election-submit" disabled={loading} className="w-full h-11 bg-black text-white rounded-md font-semibold hover:bg-zinc-800 transition-colors active:scale-[0.98] flex items-center justify-center gap-2">
            {loading && <SpinnerGap size={18} className="animate-spin" />} Create election
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const [elections, setElections] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/elections").then(({ data }) => setElections(data)).catch(() => setElections([]));
  useEffect(() => { load(); }, []);

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Elections</h1>
          <p className="text-zinc-500 mt-1">Create, publish and monitor your organisation's votes.</p>
        </div>
        {!user?.demo_viewer && <CreateDialog onCreated={(el) => nav(`/app/elections/${el.id}`)} />}
      </div>

      {elections === null ? (
        <p className="text-zinc-400 font-mono text-sm">loading…</p>
      ) : elections.length === 0 ? (
        <div data-testid="empty-elections" className="border border-dashed border-slate-300 rounded-2xl bg-white p-16 text-center">
          <div className="w-14 h-14 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users size={26} className="text-zinc-400" weight="duotone" />
          </div>
          <h3 className="font-display font-bold text-xl tracking-tight">No elections yet</h3>
          <p className="text-zinc-500 text-sm mt-1 mb-6">Create your first election to get started.</p>
          <CreateDialog onCreated={(el) => nav(`/app/elections/${el.id}`)} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
          {elections.map((el) => (
            <button
              key={el.id}
              data-testid={`election-card-${el.id}`}
              onClick={() => nav(`/app/elections/${el.id}`)}
              className="text-left bg-white border border-slate-200 rounded-2xl shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <StatusChip status={el.status} />
                <ArrowRight size={18} className="text-zinc-300 group-hover:text-black group-hover:translate-x-1 transition-all" weight="bold" />
              </div>
              <h3 className="font-display font-bold text-xl tracking-tight leading-snug mb-1 line-clamp-2">{el.title}</h3>
              <p className="text-sm text-zinc-500 line-clamp-2 mb-5 min-h-[2.5rem]">{el.description || "No description"}</p>
              {(el.status === "live" || el.status === "closed" || el.status === "scheduled") ? (
                <TurnoutBar turnout={el.turnout} />
              ) : (
                <p className="text-xs font-mono text-zinc-400">DRAFT · NOT PUBLISHED</p>
              )}
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
}
